/**
 * A Studio project as a schedule submission — STUDIO_DESIGN.md §6.2, §6.5, §3.3.
 *
 * PURE. No services, no container, no network, no React. Everything this module decides is
 * decided from its arguments, which is what makes the revision rule, the index remap and the
 * edge preference testable without a browser. The two model classes it constructs for a restored
 * input (`restoredUploadOf`, S-44) are models, not services: no container edge.
 *
 * Five things happen here and nowhere else:
 *
 * 1. **Dedupe and REMAP.** Two clips can point at the same upload — Duplicate is a button in the
 *    inspector — and `clip.input` indexes `originalInputFiles` (§3.1). Sending the same upload
 *    twice would work, but it would reference the same bytes twice and make ironman's per-clip
 *    cost accounting lie. Deduping WITHOUT remapping would be a bug: every index past the
 *    duplicate would shift and the wrong photo would render. So both, together, in one place.
 *
 * 2. **The revision.** `touchForExport` is P5a's single bump path and it takes the deduped input
 *    ids, which only this module knows. Calling it here means the Phase 3 carry-forward — "the
 *    editor bumps `revision` on ANY export-affecting change (compression group included) or users
 *    meet 409 schedule_key_conflict" — is discharged by construction: the options summary is one
 *    of the three inputs to the signature.
 *
 * 3. **The carrier.** The composition rides inside the stringified `options` under the key
 *    `composition` (§3.1, D-1), which is the detection predicate both tiers agree on
 *    (`CompositionOptions.compositionObject`). `optionsSnapshot` stays composition-FREE (§11.0
 *    R11): it is what the Quick-convert re-edit modal merges into a fresh options tree, and a
 *    composition in there would be handed to an editor that has no idea what it is.
 *
 * 4. **The edge preference.** The inputs' upload HOSTS, most bytes first (§4.5 v1) — see
 *    `edgeHostOf`, which exists because `StoredClipRef.uploadDomain` is not what its name says.
 *
 * 5. **The name.** `StudioExportRequest.fileName` (P5b) reaches the card here rather than being
 *    dropped, and is sanitised on the way (P5b interface rule 21). It ALSO rides the wire as a
 *    `fileName` sibling of `composition` inside `options` (BUG-24), RAW and un-sanitised — ironman
 *    `767c3f3c` already sanitises that key on arrival — omitted when blank so a missing key falls
 *    back to today's behaviour. Never inside `composition` itself (`additionalProperties: false`
 *    would refuse the request) and never in `optionsSnapshot` (renaming is not a re-render).
 *
 * `imageDuration` is never written (master :24): a photo's duration is `holdMs` on its clip, and
 * an `imageDuration` in the options would be a second, disagreeing answer for the same thing.
 */
import { FileUploadRequestMetadata } from '../../../models/file-upload-request-metadata';
import { UploadInProgressFile } from '../../../models/upload-in-progress-file';
import { cloneDeep } from '../../../utils/lodash-min';
import type { ConvertOptionsSummary } from '../../convert-options-v2/convert-options.context';
import type {
    ExportSubmission, Frame, ProbeInfo, ProbeMap, StudioCompositionV1, StudioDocument, VisualClip,
} from '../model/composition';
import { toWire, touchForExport } from '../model/composition';
import type { StoredClipRef } from '../state/persistence';

/** The output container the Studio pins, as the option tree spells it (`propertyValue`). */
export const STUDIO_TO_EXTENSION = 'MP4';

/** The same container as a file suffix, for the card's label. */
export const STUDIO_CONTAINER_EXT = 'mp4';

/** What the sheet seeds its name field with, and what a blank name falls back to. */
export const DEFAULT_EXPORT_FILE_NAME = 'my-video';

/**
 * X1-03 (review 7): the cap is the SERVER's -- ironman keeps a download name to 200 UTF-8 bytes including its
 * extension -- so the name itself gets 200 − `.mp4` = 196 bytes (196 Latin characters, 65 CJK). The old 80-character
 * cap cut a 161-character name the rest of the site keeps whole, silently. The sheet now says so while typing
 * (`exportNameNote`), and this is the only cap.
 */
export const MAX_EXPORT_FILE_NAME_BYTES = 200 - `.${'mp4'}`.length;

/** One staged file, index-aligned with the composition's `input` values. */
export interface ExportClip {
    /**
     * The live upload, or nothing after a reload. When absent, `ref.wire` (S-44) is what the
     * card is built from -- see `restoredUploadOf`. Both absent is the `unhydrated` rejection.
     */
    upload: UploadInProgressFile<any> | null | undefined;
    ref: StoredClipRef;
}

export interface ExportInput {
    /** The document as the editor holds it — NOT yet at its submission revision. */
    document: StudioDocument;
    /**
     * The canonical input list. `clip.input` is an index into this array.
     *
     * **Every entry must be HYDRATABLE: a live `upload`, or a `ref.wire` (S-44).** After a
     * reload the tray is rebuilt from `StoredClipRef`s through `clipSourceOfStored`
     * (`state/clip-source.ts`) and `StudioUploads` holds no `UploadInProgressFile` at all — so
     * an owner that resolves each input as `uploads().find(u => u.uuid === id)` gets
     * `undefined`, and `createMergeFileUpload` would dereference `.file.name` on it
     * (`utils/file-upoad.utils.ts:91`): a `TypeError` out of a click handler, on the one flow
     * persistence exists for.
     *
     * The record now carries the two wire objects the card needs (`StoredClipRef.wire`), and
     * this module re-creates the upload from them (`restoredUploadOf`) — the same shape
     * `FileUploadUtils.toUploadInProgressFile` builds for the merge modal's re-add. An entry with
     * NEITHER is refused as `{ ok: false, code: 'unhydrated' }`, naming the positions, which is
     * the case a record written before `wire` existed produces; the caller blocks Export with
     * the existing copy. The check is here rather than at the call site because this is the
     * module that knows what the card needs.
     */
    inputs: ExportClip[];
    /**
     * What the browser measured for each input, in `inputs`' index space.
     *
     * Used for TWO decisions, both only in the submitted composition. `toWire` omits a video
     * clip's `trimEndMs` when the clip's end IS the source's end and the EDGE can work that end
     * out for itself, so it reads to EOF instead of cutting at a probed stream duration that can
     * stop short of the file (QA 7b59d2a8) — never for an animated image, which `ffprobe` cannot
     * time (`ProbeInfo.animated`). And `unmeasuredInputs` refuses the build for an animated source
     * nobody has been able to time at all.
     * Optional — without it every clip carries its number, which is the pre-change behaviour.
     * `dedupe` remaps it alongside the document, because a probe keyed by an input index that has
     * just been collapsed would answer for the wrong file.
     */
    probes?: ProbeMap | null;
    /** The Studio's own Options V2 summary (the compression group the sheet mounted). */
    optionsSummary: ConvertOptionsSummary;
    /**
     * The name the user typed in the export sheet, WITHOUT an extension
     * (`StudioExportRequest.fileName`, `export-sheet.component.tsx:49-53`).
     *
     * REQUIRED rather than optional, and that is the point: P5b's interface rule 21 says this
     * field "has no home today, so the name the user types is dropped", and a required field
     * turns that silent drop into a compile error at the one call site that can fix it.
     */
    fileName: string;
}

export interface ExportRequest {
    /** `options` as the schedule call stringifies it: the summary, the pin, and the composition. */
    options: Record<string, unknown>;
    /** `optionsSnapshot`: the same summary WITHOUT the composition (§11.0 R11). */
    optionsSnapshot: Record<string, unknown>;
    /** `originalInputFiles` order — deduped, and what `clip.input` now indexes. */
    orderedUploads: UploadInProgressFile<any>[];
    /** The uploaded-file ids in the same order; `touchForExport`'s second argument. */
    inputIds: string[];
    /** Upload HOSTS, most bytes first. Bare hostnames — never an edge base URL. */
    preferredEdgeDomains: string[];
    /** The wire composition actually submitted (remapped, at its submission revision). */
    composition: StudioCompositionV1;
    /** The sanitised file name, no extension. The card's label; never part of the signature. */
    fileName: string;
}

export interface BuiltExport {
    ok: true;
    request: ExportRequest;
    /** The document at the revision that was submitted. The owner adopts this. */
    document: StudioDocument;
    /** What to remember for the next comparison (P5a's `ExportSubmission`). */
    submission: ExportSubmission;
}

/**
 * Why a project could not be turned into a submission.
 *
 * - `unhydrated` — an input has neither a live `UploadInProgressFile` nor a stored wire snapshot
 *   (see `ExportInput.inputs`). A project restored from a record written before
 *   `StoredClipRef.wire` existed, and the only one of the three a user can reach by reloading.
 * - `unaddressable` — an input has no `uploadedFileId`, which is what `clipSourceOf` leaves on a
 *   file that is still uploading (`state/clip-source.ts`, placeholder sources). The server has
 *   nothing to fetch under that index.
 * - `input-out-of-range` — a clip points past the end of the input list. `checkDocument` blocks
 *   the export before this, so reaching it means the document and the tray disagree; refusing is
 *   better than submitting a composition the schema will reject at the far end.
 * - `unmeasured` — an ANIMATED IMAGE nobody has been able to time. See `unmeasuredInputs`.
 */
export type BuildRejectionCode = 'unhydrated' | 'unaddressable' | 'input-out-of-range' | 'unmeasured';

export interface BuildRejected {
    ok: false;
    code: BuildRejectionCode;
    /**
     * The positions that caused it: indexes into `inputs` for `unhydrated`/`unaddressable`/
     * `unmeasured`, indexes into `document.clips` for `input-out-of-range` (`-1` means the music
     * clip).
     */
    indexes: number[];
}

/** Narrow on `ok` before reading `request`: a rejection carries no submission. */
export type BuildResult = BuiltExport | BuildRejected;

/**
 * The bare host inside a `StoredClipRef.uploadDomain`.
 *
 * **The field does not hold what its name says.** `storedClipRefOf` writes `ClipSource.edgeBase`
 * into it (`state/clip-source.ts:139`), and `edgeBase` is
 * `ApiConfig.suffixDomainWithHttpsEdgeEndpoint(uploadDomain)` (`:123`) — i.e.
 * `https://<host>/v2/edge` when deployed, or `/__xcdev/h/<host>/v2/edge` behind the dev proxy
 * (`services/dev-proxy.ts:64-67`). professorx matches a preferred domain against
 * `EdgeModel.aliases` with `preferredDomainList.contains(alias)`
 * (`EdgeService.kt:137-142`) — BARE hostnames — so sending the base URL would match nothing,
 * fall through to `edges[RANDOM.nextInt(...)]`, and silently give up the whole point of §4.5:
 * the render would land anywhere and the serial cross-edge pull would move the most bytes
 * instead of the fewest. Nothing downstream can detect that; it just renders slower.
 *
 * Written here rather than imported from `services/dev-proxy.ts` because this module takes no
 * import edge into `services/` (the 11-service inversify SCC — `reference_fe_circular_import_tdz`).
 * The proxy's prefix is `/__xcdev/h`, and `build-request.spec.ts` pins both shapes.
 */
export function edgeHostOf(base: string | null | undefined): string {
    const raw = String(base ?? '').trim().split(/[?#]/)[0];
    if (!raw) return '';
    const noScheme = raw.replace(/^[A-Za-z][A-Za-z0-9+.-]*:\/\//, '');
    let segments = noScheme.split('/').filter(segment => segment.length > 0);
    if (segments[0] === '__xcdev' && segments[1] === 'h') segments = segments.slice(2);
    return (segments[0] || '').toLowerCase();
}

/**
 * The typed name, made safe to put on a card.
 *
 * It is a LABEL, not a path: the bytes the edge writes are named by ironman, and nothing here
 * reaches a filesystem. It is still sanitised, because it is shown on the /downloads card, in the
 * card's "Download Links" dialog (an HTML string -- escaped there since review 7, `5e62f0a81`), is
 * persisted to localStorage, and becomes the saved file's name (ironman's Content-Disposition,
 * RFC 5987 since `2622ed72`).
 *
 * Review 7 S2-04 (reviewed from the DOWNLOADS lane's patch): the old rule kept ASCII letters, digits,
 * space and `_ . ( ) -` only, so "Summer trip 2026 día 1" became "Summer trip 2026 da 1" and "旅行 video"
 * lost its words. Every script is kept now (NFC-normalised), and only what is genuinely unsafe goes:
 *   - control characters (`\p{Cc}`), lone surrogates, the BOM, and the BIDI controls -- the embeddings,
 *     overrides and isolates U+202A–U+202E / U+2066–U+2069 and the marks U+200E/U+200F/U+061C, which
 *     can make "evil\u202Egnp.mp4" display as "evilmp4.png". ZWJ / ZWNJ are KEPT: they are spelling in
 *     Persian, Hindi and emoji sequences (the patch's `\p{C}` would have removed them);
 *   - the path and shell characters `< > : " / \ | ? *`;
 *   - leading dots, and trailing dots and spaces (Windows drops or refuses them on save).
 * The cap is `MAX_EXPORT_FILE_NAME_BYTES` of UTF-8 (the server's budget, X1-03) -- a filesystem counts bytes --
 * cut on a GRAPHEME boundary so no accent, emoji or surrogate pair is split.
 *
 * A trailing `.mp4` is dropped because the sheet renders the extension separately
 * (`export-file-extension`), so someone who types "clip.mp4" means "clip" — while `holiday.2026`
 * keeps its dot, because guessing that a trailing group of digits is an extension would rename
 * the user's project behind their back.
 */
export function sanitizeExportFileName(raw: string | null | undefined): string {
    const trimmed = String(raw ?? '').normalize('NFC').trim().replace(/\.mp4$/i, '');
    const cleaned = trimmed
        .replace(/\s+/g, ' ')                                   // a tab or newline is a space, not a deletion
        .replace(UNSAFE_NAME_CHARS, '')
        .replace(/ {2,}/g, ' ')
        .replace(/^[.\s]+/, '')
        .replace(/[.\s]+$/, '');
    return capUtf8Graphemes(cleaned, MAX_EXPORT_FILE_NAME_BYTES, MAX_EXPORT_FILE_NAME_BYTES).replace(/[.\s]+$/, '') || DEFAULT_EXPORT_FILE_NAME;
}

/** S2-04: controls, lone surrogates, BOM, bidi controls, and `< > : " / \ | ? *` (see `sanitizeExportFileName`). */
const UNSAFE_NAME_CHARS = /[\p{Cc}\p{Cs}\uFEFF\u061C\u200E\u200F\u202A-\u202E\u2066-\u2069<>:"/\\|?*]/gu;

/** At most `maxGraphemes` user-perceived characters and `maxBytes` of UTF-8, cut only between graphemes. */
function capUtf8Graphemes(text: string, maxGraphemes: number, maxBytes: number): string {
    const Segmenter = (Intl as unknown as { Segmenter?: new (l?: string, o?: { granularity: 'grapheme' }) => { segment(s: string): Iterable<{ segment: string }> } }).Segmenter;
    const graphemes: string[] = Segmenter
        ? Array.from(new Segmenter(undefined, { granularity: 'grapheme' }).segment(text), (g) => g.segment)
        : Array.from(text);                                    // code points: never splits a surrogate pair
    const encoder = new TextEncoder();
    let out = '';
    let bytes = 0;
    for (let i = 0; i < graphemes.length && i < maxGraphemes; i += 1) {
        const size = encoder.encode(graphemes[i]).length;
        if (bytes + size > maxBytes) break;
        out += graphemes[i];
        bytes += size;
    }
    return out;
}

/** Distinct upload hosts, ordered by the total bytes each one holds (§4.5 v1). */
function edgePreference(inputs: readonly ExportClip[]): string[] {
    const bytes = new Map<string, number>();
    for (const entry of inputs) {
        const host = edgeHostOf(entry.ref?.uploadDomain);
        if (!host) continue;
        bytes.set(host, (bytes.get(host) || 0) + (entry.ref.size || 0));
    }
    return [...bytes.entries()]
        .sort((a, b) => (b[1] - a[1]) || a[0].localeCompare(b[0]))
        .map(([host]) => host);
}

/**
 * Collapse repeated inputs and rewrite every `clip.input` to the surviving index.
 *
 * Order is FIRST-SEEN, never sorted: the input list is the order the user built, and the edge
 * reads `originalInputFiles` positionally.
 *
 * It assumes what `reject` has already established: every entry is hydrated, every
 * `uploadedFileId` is non-empty (an empty one would collapse unrelated files into one input),
 * and every `clip.input` is in range (a stale one would silently survive the remap).
 */
function dedupe(
    document: StudioDocument,
    inputs: readonly ExportClip[],
    probes?: ProbeMap | null,
): { document: StudioDocument; inputs: ExportClip[]; probes: ProbeMap | null } {
    const kept: ExportClip[] = [];
    const indexOfId = new Map<string, number>();
    const remap = new Map<number, number>();
    inputs.forEach((entry, original) => {
        const id = entry.ref.uploadedFileId;
        const existing = id ? indexOfId.get(id) : undefined;
        if (existing !== undefined) {
            remap.set(original, existing);
            return;
        }
        const next = kept.length;
        kept.push(entry);
        if (id) indexOfId.set(id, next);
        remap.set(original, next);
    });
    if (kept.length === inputs.length) return { document, inputs: [...inputs], probes: probes ?? null };
    const clips: VisualClip[] = document.clips.map(clip => {
        const mapped = remap.get(clip.input);
        return mapped === undefined || mapped === clip.input ? clip : { ...clip, input: mapped };
    });
    const music = document.music
        ? (() => {
            const mapped = remap.get(document.music.input);
            return mapped === undefined || mapped === document.music.input
                ? document.music
                : { ...document.music, input: mapped };
        })()
        : null;
    // The probes move with the indices they describe. Collapsing inputs 0 and 2 onto 0 must not
    // leave input 0's duration answering for the file that used to be at 2 — `toWire` would then
    // omit (or keep) a trim on the strength of the wrong file's length.
    let mappedProbes: ProbeMap | null = null;
    if (probes) {
        const next: Record<number, ProbeInfo | undefined> = {};
        remap.forEach((to, from) => {
            const probe = probes[from];
            if (probe && next[to] === undefined) next[to] = probe;
        });
        mappedProbes = next;
    }
    return { document: { ...document, clips, music }, inputs: kept, probes: mappedProbes };
}

/**
 * An `UploadInProgressFile` for a restored input, rebuilt from the record's wire snapshot (S-44).
 *
 * The SAME shape `FileUploadUtils.toUploadInProgressFile` builds when the merge modal re-adds a
 * card's inputs (`utils/file-upoad.utils.ts:25-68`): a `File`-like with the stored name, size and
 * type, the `FileUploadRequestMetadata` wrapping the stored `ProcessorMetadata`, the original
 * input as the response, and the upload marked complete. `createMergeFileUpload` reads exactly
 * those (`toFileUpload`: `.file.name`, `.file.size`, `getResponse()`, `getMetadata()`,
 * `uniqueProcessType`), and `_buildFormData` re-serialises the two objects verbatim.
 *
 * The uuid is the clip's own id, which is what the tab keys a live upload by (`u.uuid === id`),
 * so a restored input and a live one are addressed the same way by every caller.
 *
 * Written here rather than through `FileUploadUtils`, which imports `container` at module scope
 * (the 11-service inversify SCC this module keeps out of); the two model classes it needs take no
 * such edge.
 */
export function restoredUploadOf(ref: StoredClipRef): UploadInProgressFile<any> | null {
    const wire = ref?.wire;
    if (!wire?.originalInputFile?.location || !wire.processorMetadata?.uploadDomain) return null;
    const meta = (wire.originalInputFile.metadata || {}) as Record<string, unknown>;
    const file = {
        name: String(meta.fname ?? ref.name ?? wire.originalInputFile.originalName ?? ''),
        size: Number(meta.fsize ?? ref.size ?? 0) || 0,
        type: String(meta.ftype ?? ''),
        lastModified: Number(meta.flastModified ?? 0) || 0,
        slice: () => null,
    };
    const metadata = new FileUploadRequestMetadata([wire.processorMetadata], file as any);
    const upload = new UploadInProgressFile<any>(file, metadata, wire.uniqueProcessType, ref.clipId);
    upload.progress = 100;
    upload.completed = true;
    upload.error = false;
    upload.orientation = { angle: '0', isFliped: 'false' } as any;
    upload.setResponse(wire.originalInputFile);
    upload.setComplete();
    return upload;
}

/** The upload the card is minted from: the live one, else the one the record can rebuild. */
function uploadFor(entry: ExportClip): UploadInProgressFile<any> | null {
    return entry.upload || restoredUploadOf(entry.ref);
}

/**
 * The summary keys the COMPOSITION owns, and what happens to each — round 6 (SDE#4), read from
 * ironman/professorx on 2026-09-19 so the wire carries one answer per fact:
 *
 *   `mergeType`             DROPPED. `ProcessTypeDetector.detect` (springboot-shared-library
 *                           `conversions/ProcessTypeDetector.kt:16-60`) returns `compose` from
 *                           `CompositionOptions.compositionObject(options) != null` ABOVE every
 *                           `mergeType` branch, on both tiers (professorx `JobSchedulingService.kt:561`,
 *                           ironman `ConvertController.kt:578`); no other reader of the key exists in
 *                           either `src/main`.
 *   `imageDurationSelection` DROPPED. Its only readers are `FFMPEGConvertOptions.kt:740-742`
 *                           (`getStringOrDefault("imageDurationSelection")?.let { … imageDuration … }`,
 *                           null-safe) and `AVProcessor.kt:367,1313`, which a composition never
 *                           reaches (`AVProcessor.kt:183,293` throw before the plain AV path). The
 *                           value it gates, `imageDuration`, was already deleted (`holdMs` is the answer).
 *   `videoBackgroundColor`  MADE EQUAL to `composition.frame.backgroundColor`. The compose path
 *                           paints from the frame (`FilterGraphBuilder.kt:217,854`); the summary's key
 *                           is read only into `FFMPEGConvertOptions.videoBackgroundColor` (`:779`, a
 *                           nullable the non-compose processors default). QA saw `#FFFFFF` beside
 *                           `#000000` on every run. Set, not dropped, so the V2 parse is unchanged.
 *   `videoResolution`/`videoPreset`/… KEPT, but only while they AGREE with the frame; otherwise the
 *                           mode becomes `VIDEO_RESOLUTION_ORIGINAL` and the value keys go. ironman
 *                           pins W × H from the frame (`FilterGraphBuilder.kt` stage A) and
 *                           `ComposeEncoderOptions.from` (`:142-200`) takes only `crf`/`preset`/
 *                           bitrate from the V2 options -- but it derives them through
 *                           `getFFMPEGConvertOptionsV2`, where `ScaleConfig.kt:305,552-556` READS the
 *                           mode and THROWS on a preset mode without `videoPreset`. So the keys stay
 *                           parseable, and a preset the frame no longer matches (the user resized in
 *                           the frame popover after choosing 1080p) is not sent as a second answer.
 *
 * `metadataList` is trimmed to one entry for a composition in `_buildFormData`
 * (`file-process.service.ts`), cited there. `fileUploadedServerEndpointList` stays per input:
 * `ConvertService.kt:374` indexes it by input position.
 */
export function harmoniseSummary(summary: Record<string, unknown>, frame: Frame): Record<string, unknown> {
    const out: Record<string, unknown> = { ...summary };
    delete out.imageDuration;
    delete out.imageDurationSelection;
    delete out.mergeType;
    if ('videoBackgroundColor' in out && frame?.backgroundColor) out.videoBackgroundColor = frame.backgroundColor;
    if (!resolutionAgrees(out, frame)) {
        out.videoResolution = 'VIDEO_RESOLUTION_ORIGINAL';
        for (const key of ['videoPreset', 'videoFixedPreset', 'width', 'height', 'resolutionPercentage']) delete out[key];
    }
    return out;
}

/** `"1280::PIXEL"` → 1280; `"50::PERCENT"` → null (a relative value is judged against nothing here). */
function pixels(value: unknown): number | null {
    const text = String(value ?? '');
    const i = text.indexOf('::');
    if (i >= 0 && !text.slice(i + 2).toUpperCase().startsWith('PIXEL')) return null;
    const n = Number(i >= 0 ? text.slice(0, i) : text);
    return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Does the summary's resolution choice describe THIS frame? Modes that claim nothing about the
 * size (Keep original, a percentage, a mode this build does not know) agree by definition; the
 * absolute ones must match the frame exactly -- the sheet writes the frame from the choice
 * (`frameSizeForResolution` → `withFrame`), so a mismatch means the frame moved afterwards.
 */
export function resolutionAgrees(summary: Record<string, unknown>, frame: Frame): boolean {
    const mode = String(summary.videoResolution ?? '');
    const w = frame?.width;
    const h = frame?.height;
    if (!(w > 0 && h > 0)) return true;
    switch (mode) {
        case 'VIDEO_RESOLUTION_PRESET': {
            const preset = pixels(String(summary.videoPreset ?? '').replace(/p$/i, ''));
            return preset === null || preset === h;
        }
        case 'VIDEO_FIXED_RESOLUTION_PRESET': {
            const [pw, ph] = String(summary.videoFixedPreset ?? '').toLowerCase().split('x').map((v) => pixels(v));
            return pw === null || ph === null || (pw === w && ph === h);
        }
        case 'VIDEO_RESOLUTION_WIDTH': { const pw = pixels(summary.width); return pw === null || pw === w; }
        case 'VIDEO_RESOLUTION_HEIGHT': { const ph = pixels(summary.height); return ph === null || ph === h; }
        case 'VIDEO_RESOLUTION_WIDTH_HEIGHT': {
            const pw = pixels(summary.width);
            const ph = pixels(summary.height);
            return (pw === null || pw === w) && (ph === null || ph === h);
        }
        default:
            return true;
    }
}

/**
 * **An animated image whose length NOBODY has measured**, as indexes into `inputs`.
 *
 * `sniffAnimated` answers `durationMs: null` rather than a short sum whenever the bytes cannot
 * prove the WHOLE length — a truncated read, a file over `ANIMATED_TIMING_BYTES`, avif/heic
 * (whose timing lives in a `moov` the sniffer does not walk), fewer than two frames. The edge is
 * the other witness, and for an animated image it may have nothing either: `ffprobe` reports
 * `duration=N/A` for an APNG. When both are silent the editor is holding a PLACEHOLDER length
 * (`UNKNOWN_VIDEO_MS`, a flat 4 s) that nothing measured, and the export has exactly three
 * shapes, two of which are lies:
 *
 *   * send the placeholder — a duration the client invented, rendered as fact;
 *   * omit the end — "read to the end of the source", which the edge resolves by probing and
 *     lands on 0: the zero-length clip of export failure `9vbjspipq2gw`;
 *   * refuse, and say which file. This.
 *
 * A refusal is the only one of the three that does not put a number nobody measured into the
 * user's video, and it is recoverable in a way the other two are not: the sentence names the file,
 * and removing it (or re-adding it in a form that can be timed) is an action the user can take.
 * The gate in `studio-tab.component.tsx` runs the same rule before the sheet opens, so this is
 * the last line rather than the first the user meets.
 *
 * Only ANIMATED sources are refused. An ordinary video the browser has not timed yet is a
 * different case with a different answer — the edge CAN probe it, so its end is simply omitted —
 * and refusing it here would block an export that works.
 */
function unmeasuredInputs(input: ExportInput): number[] {
    const probes = input.probes;
    if (!probes) return [];
    const animatedClipInputs = new Set(
        input.document.clips.filter(clip => clip.kind === 'VIDEO').map(clip => clip.input),
    );
    const out: number[] = [];
    input.inputs.forEach((_, index) => {
        if (!animatedClipInputs.has(index)) return;
        const probe = probes[index];
        if (probe?.animated === true && probe.durationMs == null) out.push(index);
    });
    return out;
}

/**
 * Everything that must be true before a project can be a submission, checked BEFORE the
 * revision is touched — a rejected build must not bump anything.
 */
function reject(input: ExportInput): BuildRejected | null {
    const unhydrated: number[] = [];
    const unaddressable: number[] = [];
    input.inputs.forEach((entry, index) => {
        if (!entry || !entry.ref || !uploadFor(entry)) { unhydrated.push(index); return; }
        if (!entry.ref.uploadedFileId) unaddressable.push(index);
    });
    if (unhydrated.length) return { ok: false, code: 'unhydrated', indexes: unhydrated };
    if (unaddressable.length) return { ok: false, code: 'unaddressable', indexes: unaddressable };
    // Ranked after the "the bytes are not there" codes and before the index check: a file that is
    // not on the edge is a bigger problem than one whose length is unknown, and an out-of-range
    // index means the document and the tray disagree, which this rule cannot be read through.
    const unmeasured = unmeasuredInputs(input);
    if (unmeasured.length) return { ok: false, code: 'unmeasured', indexes: unmeasured };
    const count = input.inputs.length;
    const inRange = (n: unknown) => Number.isInteger(n) && (n as number) >= 0 && (n as number) < count;
    const badClips = input.document.clips
        .map((clip, index) => (inRange(clip.input) ? -2 : index))
        .filter(index => index !== -2);
    if (input.document.music && !inRange(input.document.music.input)) badClips.push(-1);
    if (badClips.length) return { ok: false, code: 'input-out-of-range', indexes: badClips };
    return null;
}

/**
 * The whole submission, including the revision it is submitted at.
 *
 * Returns a REJECTION rather than throwing when the project cannot be submitted at all — see
 * `BuildRejectionCode`, and `ExportInput.inputs` for the case a reload produces. Narrow on `ok`.
 *
 * @param last what the previous export of this project submitted, from PERSISTENCE —
 *   `storedSubmission(readProject(sid))`, never a component field (review I1). `null` on the
 *   first export of a project, which is the case `nextRevision` answers with `doc.revision`.
 */
export function buildExportRequest(input: ExportInput, last: ExportSubmission | null): BuildResult {
    const rejected = reject(input);
    if (rejected) return rejected;
    const collapsed = dedupe(input.document, input.inputs, input.probes);
    const inputIds = collapsed.inputs.map(entry => entry.ref.uploadedFileId);
    // One answer per fact (round 6, SDE#4): the keys the composition owns are dropped or made equal
    // to it BEFORE the signature is taken, so the revision rule sees the summary that goes out.
    // Never `imageDuration`: a photo's duration is `holdMs` on its clip, and a summary that
    // carried one (the Quick-convert group seeds `"5"`) would put a second answer on the wire.
    const summary: Record<string, unknown> = harmoniseSummary({
        ...(input.optionsSummary as Record<string, unknown>),
        toExtension: STUDIO_TO_EXTENSION,
    }, collapsed.document.frame);
    // The ONE bump path (P5a). The summary is one of the three signature inputs, so a quality
    // preset changed without touching the timeline is a new revision rather than a 409 — and
    // the file NAME is deliberately not in it: renaming the output is not a different render.
    const touched = touchForExport(collapsed.document, inputIds, summary, last);
    // The probes are passed HERE and nowhere else in this function: this is the composition that
    // is actually submitted, so it is the one that may omit an untrimmed `trimEndMs`. The
    // signature `touchForExport` took deliberately does not see them — a change detector that
    // flipped with probe availability would bump the revision for a timeline nobody touched.
    const composition = toWire(touched.document, collapsed.probes);
    // The wire fileName (BUG-24 client half). A SIBLING of `composition` inside `options` — never
    // inside it, since that schema is `additionalProperties: false` and would refuse the request —
    // and never in `optionsSnapshot` (renaming is not a re-render; see the module doc, point 5,
    // and the signature test in `build-request.spec.ts`). Sent RAW: ironman (`767c3f3c`) already
    // sanitises this key on arrival, so sanitising it here too would just give the two a chance to
    // disagree. Omitted entirely when blank, matching the byte-for-byte fallback ironman keeps for
    // a missing or malformed key.
    const typedFileName = input.fileName != null && input.fileName.trim() ? input.fileName : null;
    return {
        ok: true,
        request: {
            options: { ...summary, composition, ...(typedFileName !== null ? { fileName: typedFileName } : {}) },
            // Deep-cloned, like the legacy merge path
            // (`file-process-editor.component.tsx:159-161`): the snapshot is persisted with the
            // card and is what the Quick-convert re-edit modal merges into a fresh options tree,
            // so it must not share nested objects with the editor's live summary — an option
            // changed after the export would otherwise rewrite the record of what was submitted.
            optionsSnapshot: cloneDeep(summary),
            // `reject` proved every entry hydratable, so the `!` is a fact, not a hope.
            orderedUploads: collapsed.inputs.map(entry => uploadFor(entry)!),
            inputIds,
            preferredEdgeDomains: edgePreference(collapsed.inputs),
            composition,
            fileName: sanitizeExportFileName(input.fileName),
        },
        document: touched.document,
        submission: touched.submission,
    };
}
